/** Hydrogen operator primitives bound to Matrix product vocabulary. */

import type { TemplateInstance } from 'hydrogen/runtime';
import { html, repeat } from 'hydrogen/runtime';

import { EmptyBlock, type Tone } from './kit.js';

import type { ChangeRow, Measured, SurfaceSnapshot } from '../generated/surface.js';
import type { RefusableCollection } from '../generated/surface-refusals.js';
import type { SurfaceConnectionState } from './surface-connection.js';

export type StatusTone = Tone;

const DIGEST_DEFAULT = 9;

/** What a digest shows when it stands alone rather than inside a sentence. */
const DIGEST_RENDER = 12;
const MINUTE_S = 60;
const HOUR_S = 3600;
const DAY_S = 86_400;

export function count(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

/**
 * The snapshot collections the authority is able to declare it does not
 * project, derived in `ui/scripts/surface-refusals.ts` from the Worker's own
 * declarations and the protocol schema.
 *
 * This was a hand-written union of four while the authority declared
 * thirty-eight refusals, five of which name a collection. The one outside the
 * type was `states`, and nothing could guard it: `/frontier-lens` printed
 * "Observed States 1" from a window the authority had already declared
 * partial, while `/history` printed 2 from the same projection.
 */
export type { RefusableCollection };

/**
 * A collection the authority may refuse, read so that the refusal survives.
 *
 * A standing-refused array is constructed empty on every served response and
 * the projection declares it refused with its reason. Folding the array still
 * produces a number, so the surfaces printed "Conflict 0", "0 recorded runs",
 * "Attestations 0" and "Recorded pairs 0" -- a measured all-clear for a
 * measurement the authority says it never makes. That is the worst shape a gap
 * can take: it is not a missing value, it is a confident wrong one, and on the
 * causal lens it is a false all-clear about conflicts.
 *
 * A windowed collection (`states`, `recent_changes`) is the same defect at one
 * remove: the rows are real and the total is not, so a fold over them reports
 * a number no authority projected.
 *
 * Reading through here collapses "declared" into `undefined`, which every
 * value position already renders as a dash carrying its reason. A direct
 * `snapshot.algebra` read is banned by `projected-gaps.test.ts` for exactly
 * this reason: the empty array and the refusal are indistinguishable once you
 * have the array in hand -- and for a windowed collection, so are the window
 * and the whole.
 */
export function projected<K extends RefusableCollection>(
  snapshot: SurfaceSnapshot | null | undefined,
  id: K,
): SurfaceSnapshot[K] | undefined {
  if (!snapshot) return undefined;
  return snapshot.not_projected.some((entry) => entry.id === id) ? undefined : snapshot[id];
}

/**
 * The value and note of a metric folded from a collection the authority may
 * refuse: the count when it serves the rows, the dash and its own reason when
 * it does not. The note position is the only place a plain-string metric can
 * carry the reason, so a refused metric replaces the editorial note rather
 * than sitting beside one that describes rows nobody has.
 */
export function projectedCount<K extends RefusableCollection>(
  snapshot: SurfaceSnapshot | null,
  id: K,
  note: string,
  fold: (rows: SurfaceSnapshot[K]) => number = (rows) => rows.length,
): { value: string; note: string; projected: boolean } {
  return projectedMetric(snapshot, id, note, (rows) => count(fold(rows)));
}

/**
 * `projectedCount` for a metric that is not a count.
 *
 * Three states, not two. The authority declared the collection: the dash
 * carries its reason. The rows are served and none answers this metric: the
 * dash carries the note that says so, which is an observation about the rows
 * rather than a claim about the authority. Otherwise the fold.
 *
 * `/context` printed "Resident 0" and "Provenance 0" from
 * `count(runtime?.resident_handles ?? 0)` -- one `?? 0` collapsing both
 * absences into a measurement -- beside two tiles in the same row that
 * correctly rendered a dash for the same refused collection. A surface that
 * contradicts itself about one declaration has no vocabulary for the third
 * state; this is it.
 */
export function projectedMetric<K extends RefusableCollection>(
  snapshot: SurfaceSnapshot | null,
  id: K,
  note: string,
  fold: (rows: SurfaceSnapshot[K]) => string | undefined,
  whenUnanswered = note,
): { value: string; note: string; projected: boolean } {
  const rows = projected(snapshot, id);
  if (rows === undefined) {
    return { value: GAP_DASH, note: gapReason(snapshot, id), projected: false };
  }
  const value = fold(rows);
  return value === undefined
    ? { value: GAP_DASH, note: whenUnanswered, projected: false }
    : { value, note, projected: true };
}

/**
 * A reading this producer took.
 *
 * The wire shape written once. A producer spelling `{ measured: 'yes', value }`
 * inline is a second copy of the protocol, and the copy is what drifts.
 */
export function measuredAs<T>(value: T): Measured<T> {
  return { measured: 'yes', value };
}

/**
 * An absence, citing the `not_projected` id whose reason explains it.
 *
 * The id must be one this document declares. `gapReason` fails closed on one
 * it does not, so a producer citing a declaration it never made is visible on
 * the page rather than silent.
 */
export function refusedBy(refusal: string): Measured<never> {
  return { measured: 'no', refusal };
}

/**
 * The reading, where the authority took one.
 *
 * The one narrowing that hands back a bare number, for the folds, sorts, and
 * filters that have to skip what was never measured. A value position uses
 * `Measurement` instead: this drops the refusal, and a dash whose reason has
 * been dropped is the dash nobody can explain.
 */
export function reading<T>(field: Measured<T>): T | undefined {
  if (!isMeasured(field)) return undefined;
  return field.measured === 'yes' ? field.value : undefined;
}

/**
 * A measurement as text, for a position that must be a plain string.
 *
 * The `GAP_DASH` contract: the reason is carried by whatever sits beside this
 * -- a note, or the same field rendered through `Measurement` in the cell the
 * reader actually looks at. A position with nowhere to put the reason uses
 * `Measurement`, not this.
 */
export function measured<T>(field: Measured<T>, format: (value: T) => string): string {
  if (!isMeasured(field)) return GAP_DASH;
  return field.measured === 'yes' ? format(field.value) : GAP_DASH;
}

/**
 * A measurement, or the declaration the authority cited instead of one.
 *
 * `Gap` already renders a refused field as a dash carrying its own sentence;
 * what was missing was the link from the field to the declaration. The id used
 * to be written at the call site, so a reader had to know that `duration_ms`
 * was the field `fabric_run_duration` refuses. It now travels in the value,
 * and an id no declaration covers still fails closed through `gapReason`.
 */
export function Measurement<T>(
  snapshot: SurfaceSnapshot | null,
  field: Measured<T>,
  format: (value: T) => string,
): TemplateInstance | string {
  if (!isMeasured(field)) return dash(unreadableMeasurement(snapshot));
  return field.measured === 'yes' ? format(field.value) : Gap(snapshot, field.refusal);
}

/**
 * Whether a wire value is a measurement this shell can read at all.
 *
 * `isSnapshot` validates the twenty-one top-level keys and no row field, so an
 * authority still answering surface protocol 5 -- where these were `number |
 * null` -- reaches a protocol 6 shell with a bare `null` here. Reading
 * `.measured` off it threw, and a render that throws is a blank `<main>`:
 * /changes and /decisions rendered nothing at all, and /projects, /agents,
 * /context and /oracle-center never left "Connecting to Matrix".
 *
 * A measurement this shell cannot read is an absence, and the product's rule
 * for an absence is a dash carrying a sentence -- never a page that renders
 * nothing and never a number nobody measured.
 */
export function isMeasured<T>(field: Measured<T>): boolean {
  const value: unknown = field;
  return typeof value === 'object' && value !== null && 'measured' in value;
}

/**
 * What an unreadable measurement says, naming the protocol that sent it.
 *
 * The snapshot carries its own protocol, so the sentence reads the authority's
 * number rather than restating this shell's -- one source, and it stays true
 * as both move.
 */
export function unreadableMeasurement(snapshot: SurfaceSnapshot | null): string {
  if (!snapshot) {
    return 'No authority projection has been read, so this measurement has no source.';
  }
  return `This authority answers with surface protocol ${String(snapshot.protocol)}, which does not carry this measurement.`;
}

/**
 * The value, note, and projected flag of a metric tile reading one measurement:
 * the reading and its editorial note, or the dash and the authority's reason.
 *
 * The same shape `projectedCount` returns for a refused collection, because a
 * tile cannot tell the reader why a dash is there except in its note.
 */
export function measuredTile<T>(
  snapshot: SurfaceSnapshot | null,
  field: Measured<T>,
  note: string,
  format: (value: T) => string,
): { value: string; note: string; projected: boolean } {
  if (!isMeasured(field)) {
    return { value: GAP_DASH, note: unreadableMeasurement(snapshot), projected: false };
  }
  return field.measured === 'yes'
    ? { value: format(field.value), note, projected: true }
    : { value: GAP_DASH, note: gapReason(snapshot, field.refusal), projected: false };
}

/**
 * Whether a collection has nothing in it.
 *
 * `xs.length === 0` states a mechanism where the surfaces mean a question, and
 * every render position asking it spelled the mechanism out. One predicate
 * says what is being asked.
 */
export function isEmpty(items: { readonly length: number } | null | undefined): boolean {
  return !items || items.length === 0;
}

export function percent(numerator: number, denominator: number): string {
  if (denominator === 0) return '—';
  return `${Math.round((numerator / denominator) * 1000) / 10}%`;
}

/**
 * The dash itself, for the one place no absence constructor can reach: a component whose
 * value must be a plain string, such as a Hydrogen `MetricCard`. Those print a
 * note beside the value, so the reason is already on the page; naming the dash
 * says which of the two contracts a call site is under.
 */
export const GAP_DASH = '—';

export function short(value: string | null | undefined, size = DIGEST_DEFAULT): string {
  if (!value) return '—';
  return value.length <= size ? value : `${value.slice(0, size)}…`;
}

/** Certified state roots on the authority Frontier, in wire order. */
export function frontierStateRoots(snapshot: SurfaceSnapshot): string[] {
  return snapshot.canonical.frontier.map((point) => point.state_root);
}

/** Compact set rendering that never elects one Frontier point. */
export function frontierSummary(snapshot: SurfaceSnapshot, size = DIGEST_DEFAULT): string {
  const roots = frontierStateRoots(snapshot);
  if (roots.length === 0) return '—';
  return roots.map((root) => short(root, size)).join(', ');
}

/** Total files represented by the visible Frontier points. */
export function frontierFileCount(snapshot: SurfaceSnapshot): number {
  return snapshot.canonical.frontier.reduce((total, point) => total + point.file_count, 0);
}

/** Distinct transaction epochs carried by Frontier points. */
export function frontierEpochSummary(snapshot: SurfaceSnapshot): string {
  const epochs = [...new Set(snapshot.canonical.frontier.map((point) => point.epoch))].sort(
    (left, right) => left - right,
  );
  if (epochs.length === 0) return '—';
  return epochs.map((epoch) => String(epoch)).join(', ');
}

/** Whether `root` is one of the currently valid Frontier points. */
export function isOnFrontier(snapshot: SurfaceSnapshot, root: string): boolean {
  return snapshot.canonical.frontier.some((point) => point.state_root === root);
}

/** How far back a host clock reading sits from this snapshot's own clock. */
export function relativeAge(snapshot: SurfaceSnapshot, unix: number): string {
  const seconds = Math.max(0, snapshot.generated_at_unix - unix);
  if (seconds < MINUTE_S) return `${seconds}s ago`;
  if (seconds < HOUR_S) return `${Math.floor(seconds / MINUTE_S)}m ago`;
  if (seconds < DAY_S) return `${Math.floor(seconds / HOUR_S)}h ago`;
  return `${Math.floor(seconds / DAY_S)}d ago`;
}

/**
 * `relativeAge` of a reading the authority may not have taken.
 *
 * The old signature took `number | null` and answered "not observed" for the
 * null -- the render layer stating a fact about what the authority stores,
 * beside a `not_projected` declaration that states the same fact in the
 * authority's own words. This asks the value which declaration governs it.
 */
export function ago(
  snapshot: SurfaceSnapshot,
  at: Measured<number>,
): TemplateInstance | string {
  return Measurement(snapshot, at, (unix) => relativeAge(snapshot, unix));
}

export function acceptedIds(snapshot: SurfaceSnapshot): Set<string> {
  return new Set(snapshot.certificates.flatMap((certificate) => certificate.changes));
}

export function projectName(snapshot: SurfaceSnapshot): string {
  return snapshot.product || 'Matrix project';
}

export function isValidAuthority(snapshot: SurfaceSnapshot): boolean {
  return snapshot.canonical.frontier.length > 0 && snapshot.oracles.fail === 0;
}

/**
 * One predicate for one claim. The sidebar previously derived "operational"
 * from `oracles.fail === 0` while the Home panel used isValidAuthority, so an
 * authority with nothing in it reported operational and needs-attention on the
 * same screen.
 *
 * An authority that holds nothing is not degraded — it is empty, and saying so
 * keeps the alarm colour for something consequential.
 */
export type AuthorityState = 'operational' | 'empty' | 'attention' | 'stale' | 'unknown';

/**
 * What this surface is allowed to claim, folded from the projection *and* the
 * channel that keeps it current.
 *
 * `connection` is required rather than defaulted. A default would be a claim
 * about the transport made by every call site that had not thought about it,
 * and that is precisely the failure this fold exists to end: the headline read
 * `oracles.fail === 0 && frontier.length > 0` — a faithful reading of
 * *authority* health — and rendered it as "All systems operational" beside a
 * dead event stream that had never delivered a frame. The shell recorded the
 * truth the whole time (`data-live="false"`, "Surface reconnecting"); the
 * headline simply never consulted it.
 *
 * An authority fault still outranks a stale channel: a failing oracle is a
 * fact this surface holds, while a dropped stream only means it may be holding
 * an old one.
 */
export function authorityState(
  snapshot: SurfaceSnapshot | null,
  connection: SurfaceConnectionState,
): AuthorityState {
  // No snapshot is not an observation about the authority. Reporting "empty"
  // here would assert something unobserved; reporting "needs attention" would
  // raise an alarm for a request that has not returned.
  if (!snapshot) return 'unknown';
  if (snapshot.oracles.fail > 0) return 'attention';
  if (connection !== 'live') return 'stale';
  return snapshot.canonical.frontier.length > 0 ? 'operational' : 'empty';
}

export function authorityStateLabel(state: AuthorityState): string {
  switch (state) {
    case 'operational':
      return 'All systems operational';
    case 'empty':
      return 'No certified state yet';
    case 'attention':
      return 'Authority needs attention';
    case 'stale':
      return 'Not receiving live updates';
    case 'unknown':
      return 'Connecting to the authority';
    default: {
      const exhausted: never = state;
      return exhausted;
    }
  }
}

/** VALID, DEGRADED, or the honest third answer for an authority holding nothing. */
export function authorityStatusBadge(state: AuthorityState): string {
  switch (state) {
    case 'operational':
      return 'VALID';
    case 'attention':
      return 'DEGRADED';
    case 'empty':
      return 'NO STATE';
    // VALID would be a claim about a reading nothing is refreshing. The badge
    // says which of the two it is rather than averaging them into one word.
    case 'stale':
      return 'NOT LIVE';
    case 'unknown':
      return 'CONNECTING';
    default: {
      const exhausted: never = state;
      return exhausted;
    }
  }
}

export function statusForChange(snapshot: SurfaceSnapshot, change: ChangeRow): string {
  if (acceptedIds(snapshot).has(change.id)) return 'CERTIFIED';
  const runs = projected(snapshot, 'fabric_runs');
  // A refused collection is not an empty one. Reading `fabric_runs` off the
  // snapshot made every non-certified change `PROPOSED`, because the rows the
  // decision is read from are declared absent on every served response. That
  // put REJECTED, UNPROVABLE and CERTIFYING out of reach and pinned the
  // escalation count to a confident zero -- `/decisions` answering "0
  // decisions need you now" from a measurement the authority says it never
  // makes. The certified branch above survives because it reads certificates,
  // which are projected.
  if (runs === undefined) return 'UNPROJECTED';
  const run = runs.find((candidate) => candidate.change_id === change.id);
  if (!run) return 'PROPOSED';
  const decision = run.decision.toUpperCase();
  if (decision.includes('REJECT')) return 'REJECTED';
  if (decision.includes('UNPROV')) return 'UNPROVABLE';
  if (decision.includes('CERT')) return 'CERTIFIED';
  return decision || 'PROPOSED';
}

export function statusTone(status: string): StatusTone {
  if (
    status === 'CERTIFIED' ||
    status === 'VALID' ||
    status === 'PASS' ||
    status === 'PASSING' ||
    status === 'VERIFIED' ||
    status === 'ACCEPT' ||
    status === 'READY FOR AGENT'
  ) {
    return 'good';
  }
  if (status === 'REJECTED' || status === 'FAIL' || status === 'DEGRADED' || status === 'BLOCKED') return 'bad';
  if (status === 'UNPROVABLE' || status === 'CERTIFYING' || status === 'DRAFT') return 'warn';
  if (status === 'OBSERVED' || status === 'BETA' || status === 'PROJECTED') {
    return 'neutral';
  }
  if (status === 'SELECTED' || status === 'CURRENT') return 'active';
  return 'active';
}

export function producerIdentity(id: string): { name: string; handle: string; initials: string } {
  const tail = (id.split('/').at(-1) ?? id).replace(/^@/, '');
  const name = tail.length > 0 ? tail : 'Developer';
  return {
    name,
    handle: `@${name.replace(/[^a-zA-Z0-9._-]+/g, '').toLowerCase() || 'developer'}`,
    initials: name.slice(0, 2).toUpperCase(),
  };
}

export function primaryProducerId(snapshot: SurfaceSnapshot): string {
  return snapshot.recent_changes.at(-1)?.producer ?? 'Developer';
}

/** Compact bar series from stored counts. Never a decorative dummy line. */
export function ProductSpark(values: readonly number[]): TemplateInstance {
  const peak = Math.max(1, ...values);
  return html`<span class="mx-spark" role="img" aria-label="Stored series">${repeat(
    values,
    (_, index) => String(index),
    (value) => html`<i style="height: ${String(Math.max(8, Math.round((value / peak) * 100)))}%"></i>`,
  )}</span>`;
}

/**
 * A stated gap: what this surface does not hold, and why.
 *
 * The vendored EmptyState sets its title as an h3. A gap notice usually sits
 * directly under the page title, so without a rung between them the outline
 * stepped h1 to h3 and a reader navigating by heading fell straight past it.
 * The rung is hidden because on screen the notice is plainly a notice.
 */
/**
 * A digest, identity, or state root.
 *
 * docs/design/brand/README.md § Typography: "Digests, identities, and state
 * roots are data: they belong in mono, not in a bold sans sentence." They were
 * interpolated straight into whatever element happened to hold them, so
 * whether one landed in mono was decided by the container -- a metric's
 * <strong> was mono, a detail list's <dd> was not, and the same digest read as
 * data in one panel and as prose in the next.
 *
 * Routing them through here settles it in one place, and carries the full
 * value on the element so truncation never loses it.
 */
export function Digest(value: string | null | undefined, size = DIGEST_RENDER): TemplateInstance {
  if (!value) return Absent('No identity is stored for this field');
  return html`<span class="mx-digest" title="${value}">${short(value, size)}</span>`;
}

/** A whole string that is nothing but a hash: 32 hex characters or more. */
const IDENTITY_TOKEN = /^[0-9a-f]{32,}$/i;

/**
 * A field the authority fills with either a sentence or an identity.
 *
 * An event's `subject` is one column of the same wire field for every kind:
 * `certified` names a certificate digest, an intent names a sentence. The
 * activity stream printed it straight, so a certification event put the whole
 * 64-character digest on the page in a font sized for prose -- the one value
 * every other surface in the product cuts to twelve and carries whole on the
 * element. It also set the row's min-content width to 361px, which is how a
 * single event pushed three elements out of a 375px viewport.
 *
 * An identity is cut like every other identity here. A sentence is a sentence
 * and is left alone: cutting `oracle://worker/cargo/fmt` at twelve characters
 * would destroy a value a reader can actually read.
 */
export function Subject(value: string): TemplateInstance {
  return IDENTITY_TOKEN.test(value) ? Digest(value) : html`${value}`;
}

/**
 * A value the authority does not hold.
 *
 * "Not projected" was written into 68 value positions across the product, in
 * three different casings, usually beside a label that already named the
 * field. In a value position those words say only what the em dash beside
 * them already said, in Matrix's own vocabulary rather than the reader's --
 * and repeated down a column they read as data.
 *
 * A missing value is a dash. The reason travels with it rather than beside
 * it, so a screen reader hears why the cell is empty and a list of empty
 * cells still scans as empty.
 */
function dash(reason: string): TemplateInstance {
  return html`<span class="mx-gap-value" title="${reason}" aria-label="${reason}">—</span>`;
}

/**
 * A field this authority declares it cannot project.
 *
 * The reason is authority state, not copy. It used to be an English string
 * written into the component, which made the render layer assert a fact about
 * what the authority can compute -- a second source that drifts the moment the
 * authority gains or loses the capability. `not_projected` already carries the
 * id and the reason; this reads them.
 *
 * An id the authority does not declare fails closed: the surface says the
 * projection made no such declaration rather than inventing one, and
 * `ui/tests/projected-gaps.test.ts` refuses it at build time.
 */
export function Gap(snapshot: SurfaceSnapshot | null, id: string): TemplateInstance {
  return dash(gapReason(snapshot, id));
}

/**
 * The declared reason, or the sentence for each way there is not one.
 *
 * Four states, and this read two of them.
 *
 * With no snapshot -- the state every surface is in until the first projection
 * arrives -- it answered "This authority declared no reason", which says an
 * authority was asked and had nothing to say. None was asked. That is the
 * render layer asserting a fact about a projection it has not read, and it is
 * the first thing a person sees on every route carrying a gap.
 *
 * The other collapse is the skew. The shell asks about the ids the worker
 * source beside it declares, and the authority answering is a deploy on its
 * own clock: measured live, the shell asked about `attempt_runtime` and
 * `fabric_data_movement`, declared by surface protocol 6, while the authority
 * answered protocol 5 and carried neither. "Declared no reason" says the
 * authority was asked and declined to explain. It was not asked: the document
 * it sent has no such declaration to make. That reads the same way as
 * `unreadableMeasurement` one layer down, which is the same fact about the
 * same skew -- so it names the protocol rather than the authority's intent,
 * and `/history` already prints that form for a measurement.
 *
 * What survives is the state those two were standing in for: a projection that
 * does carry the declaration and left its reason empty. That one is an
 * authority asked and silent, and it keeps the sentence, because a fail-closed
 * rule whose sentence no producer can emit is a rule that never fires.
 */
export function gapReason(snapshot: SurfaceSnapshot | null, id: string): string {
  if (!snapshot) {
    return `No authority projection has been read, so nothing is yet declared about \u201c${id}\u201d.`;
  }
  const declared = snapshot.not_projected.find((entry) => entry.id === id);
  if (!declared) {
    return `This authority answers with surface protocol ${String(snapshot.protocol)}, which carries no declaration for \u201c${id}\u201d.`;
  }
  return declared.reason.trim().length > 0
    ? declared.reason
    : `This authority declared no reason for the missing field \u201c${id}\u201d.`;
}

/**
 * `Gap` where a whole region is missing rather than one value.
 *
 * `Gap` carries its reason in a tooltip because a value position has room for
 * one character. A panel has room for the sentence, and a tooltip is not
 * reachable by touch or by tab, so a region the authority cannot fill says why
 * in text. Both read the same declaration: the reason is authority state
 * either way, and the shape of the hole decides only where it is printed.
 */
export function GapNotice(snapshot: SurfaceSnapshot | null, id: string): TemplateInstance {
  return html`<p class="mx-gap-notice">${sentence(gapReason(snapshot, id))}</p>`;
}

/** A declared reason, which is a clause, set as prose. */
export function sentence(reason: string): string {
  const capitalised = `${reason.slice(0, 1).toUpperCase()}${reason.slice(1)}`;
  return capitalised.endsWith('.') ? capitalised : `${capitalised}.`;
}

/**
 * The line a list shows when it has no rows.
 *
 * "No relation verdict observed" and "No effect attestation" are observation
 * claims: they say the authority looked. When it has declared it cannot look,
 * its own sentence replaces them, so an empty list never reports a search that
 * did not happen.
 */
export function absenceLine<K extends RefusableCollection>(
  snapshot: SurfaceSnapshot | null,
  id: K,
  whenEmpty: string,
): string {
  return projected(snapshot, id) === undefined ? sentence(gapReason(snapshot, id)) : whenEmpty;
}

/**
 * A field the authority does project, on a record that has no value for it.
 *
 * Distinct from `Gap` on purpose: "the authority cannot compute this" and
 * "this row is empty" are different facts, and rendering both as one dash is
 * how the first became invisible.
 */
export function Absent(reason: string): TemplateInstance {
  return dash(reason);
}

/** A measurement the isolated native runner has not produced. Never authority state. */
export function Unmeasured(reason: string): TemplateInstance {
  return dash(reason);
}

/** A value waiting on the person choosing something, not on the authority. */
export function Unselected(reason: string): TemplateInstance {
  return dash(reason);
}

/** A region the authority does not project, said once: what is absent and why. */
export function ProductGap(options: { title: string; detail: string; issue?: string }): TemplateInstance {
  return html`<div class="mx-gap" data-issue="${options.issue ?? ''}">
    <h2 class="mx-visually-hidden">Surface status</h2>
    ${EmptyBlock({ title: options.title, detail: options.detail })}
  </div>`;
}
